import { useCallback, useMemo } from 'react';
import { connect, useDispatch } from 'react-redux';
import { groupBy } from 'ramda';
import type { Dispatch } from 'redux';

import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { Modal } from 'UIPackage/ModalModule/components/Modal';
import { deleteObjectProperties } from 'CorePackage/MainModule/common/deleteObjectProperties';
import { ISmevMonitoringIndex } from 'APIPackage/SmevMonitoringModule/interfaces/ISmevMonitoringIndex';
import { Textarea } from 'UIPackage/MainModule/components/Textarea';
import { DATE_FORMAT_FULL_FNS } from 'CorePackage/MainModule/constants/dateFormats';
import { formatDate } from 'CorePackage/MainModule/common/formatDate';
import { ModalHeader } from 'UIPackage/ModalModule/components/ModalHeader';
import { ModalBody } from 'UIPackage/ModalModule/components/ModalBody';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';

import { smevMonitoringSetMessageById } from '../actions/smevMonitoringActions';
import { getSmevMonitoringMessageById } from '../selectors/smevMonitoringSelectors';
import styles from './SmevMonitoringAnswerChainModal.module.scss';

interface ISmevMonitoringAnswerChainModalStoreProps {
  messageById: ISmevMonitoringIndex | undefined;
}

const SmevMonitoringAnswerChainModalWithoutStore = ({ messageById }: ISmevMonitoringAnswerChainModalStoreProps) => {
  const dispatch = useDispatch<Dispatch<RootActions>>();

  const handleCloseAnswerChainModal = useCallback(() => {
    dispatch(smevMonitoringSetMessageById());
  }, [dispatch]);

  const rowInfo = useMemo(() => {
    if (messageById) {
      const keys = groupBy<keyof ISmevMonitoringIndex>(
        item =>
          (
            item.includes('receive') ||
            (item.includes('send') && !item.includes('sender')) ||
            item.includes('smev')
          ).toString(),
        Object.keys(messageById) as (keyof ISmevMonitoringIndex)[],
      );

      return {
        default: Object.entries(deleteObjectProperties(messageById, ['id', ...keys.true])),
        message: Object.entries(deleteObjectProperties(messageById, [...keys.false])),
      };
    }
  }, [messageById]);

  if (!messageById) {
    return null;
  }

  return (
    <Modal modalBodyClassName={styles.ModalBody} visible={!!messageById} onClose={handleCloseAnswerChainModal}>
      <ModalHeader title={`Цепочка сообщений ${messageById.id}`} sizeText="small" color="black" />
      <ModalBody size="content" border="bottom" overflow="scroll">
        <div className={styles.SectionItem}>
          {rowInfo?.default.map(item => (
            <div className={styles.InfoItem} key={item[0]}>
              {item[0]}:<span className={styles.InfoValue}>{item[1] || ''}</span>
            </div>
          ))}
        </div>
        <div className={styles.SectionItem}>
          {rowInfo?.message.map(item => {
            const isContent = item[0].includes('Content');
            const isHead = item[0].includes('Uid');
            const infoItemAttributes = {
              ...(isContent && { 'data-content': true }),
              ...(isHead && { 'data-head': true }),
            };

            return (
              <div className={styles.InfoItem} key={item[0]} {...infoItemAttributes}>
                {item[0]}:
                {item[1] && isContent ? (
                  <Textarea className={styles.InfoArea} resize="vertical" value={item[1]} autoHeight={false} />
                ) : (
                  <span className={styles.InfoValue}>
                    {(item[0].includes('Date') || item[0].includes('At')) && item[1]
                      ? formatDate({
                          date: item[1],
                          dateFormat: DATE_FORMAT_FULL_FNS,
                        })
                      : item[1]}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      </ModalBody>
    </Modal>
  );
};

const mapStateToProps = (state: IRootState): ISmevMonitoringAnswerChainModalStoreProps => ({
  messageById: getSmevMonitoringMessageById(state),
});

export const SmevMonitoringAnswerChainModal = connect(mapStateToProps)(SmevMonitoringAnswerChainModalWithoutStore);
